import { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded'
import GavelRoundedIcon from '@mui/icons-material/GavelRounded'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import IncidentsAPI from 'services/IncidentsAPI'
import { searchAssignableUsers } from 'services/taskService'
import SearchableSelect from 'components/SearchableSelect'
import { RAG } from 'features/dashboard/charts/scoreColour'

const INVESTIGATION_STATUSES = {
  not_required: 'Not required',
  pending:      'Pending',
  in_progress:  'In progress',
  completed:    'Completed',
}

const INVESTIGATION_STATUS_RAG = {
  not_required: RAG.none.fg,
  pending:      RAG.medium.fg,
  in_progress:  RAG.medium.fg,
  completed:    RAG.low.fg,
}

const ROOT_CAUSES = {
  equipment_failure: 'Equipment failure',
  human_error:       'Human error',
  procedure_gap:     'Procedure gap / not followed',
  environmental:     'Environmental conditions',
  third_party:       'Third party',
  design_defect:     'Design defect',
  maintenance_lapse: 'Maintenance lapse',
  unknown:           'Not yet determined',
}

const IncidentInvestigationPanel = ({ incident, onUpdated }) => {
  const [users, setUsers] = useState([])
  const [statements, setStatements] = useState([])
  const [saving, setSaving] = useState(false)
  const [addingWitness, setAddingWitness] = useState(false)
  const [witnessDraft, setWitnessDraft] = useState({ name: '', contact: '', statement: '' })
  const [noteDraft, setNoteDraft] = useState(incident.investigationNote || '')
  const [riddorRefDraft, setRiddorRefDraft] = useState(incident.riddorReference || '')

  const overdue = incident.investigationDueAt
    && ['pending', 'in_progress'].includes(incident.investigationStatus)
    && new Date(incident.investigationDueAt) < new Date()

  const riddorUrgent = incident.riddorReportable
    && !incident.riddorReportedAt
    && incident.riddorDeadline
    && (new Date(incident.riddorDeadline).getTime() - Date.now()) < 3 * 86400000

  useEffect(() => {
    searchAssignableUsers({ search: '', buildingPassportId: incident.buildingPassportId })
      .then(res => setUsers(res?.data?.data || []))
      .catch(() => {})
  }, [incident.buildingPassportId])

  const loadStatements = useCallback(() => {
    IncidentsAPI.listWitnessStatements(incident.id)
      .then(res => setStatements(res?.data?.data?.statements || []))
      .catch(() => {})
  }, [incident.id])

  useEffect(() => { loadStatements() }, [loadStatements])

  const patch = async (payload, successMsg = 'Saved.') => {
    setSaving(true)
    try {
      await IncidentsAPI.update(incident.id, payload)
      toast.success(successMsg)
      onUpdated?.()
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not save.')
    } finally {
      setSaving(false)
    }
  }

  const addWitness = async () => {
    if (!witnessDraft.name.trim() || !witnessDraft.statement.trim()) return
    try {
      await IncidentsAPI.addWitnessStatement(incident.id, {
        witness_name: witnessDraft.name,
        witness_contact: witnessDraft.contact || null,
        statement: witnessDraft.statement,
      })
      toast.success('Witness statement recorded.')
      setWitnessDraft({ name: '', contact: '', statement: '' })
      setAddingWitness(false)
      loadStatements()
    } catch (e) {
      toast.error('Could not record statement.')
    }
  }

  return (
    <Panel>
      <PanelHead>
        <PanelTitle>Investigation</PanelTitle>
        {overdue && <OverdueChip>Overdue</OverdueChip>}
      </PanelHead>

      <FieldRow>
        <Field>
          <FieldLabel>Status</FieldLabel>
          <SearchableSelect
            value={incident.investigationStatus || 'not_required'}
            onChange={val => patch({ investigation_status: val }, 'Investigation status updated.')}
            options={Object.entries(INVESTIGATION_STATUSES).map(([v, l]) => ({ value: v, label: l, color: INVESTIGATION_STATUS_RAG[v] }))}
            disabled={saving}
            ariaLabel='Status'
          />
        </Field>
        <Field>
          <FieldLabel>
            <PersonSearchRoundedIcon style={{ fontSize: 13 }} />
            Investigator
          </FieldLabel>
          <SearchableSelect
            value={incident.investigatorUserId}
            onChange={val => patch({ investigator_user_id: val }, 'Investigator assigned.')}
            options={users.map(u => ({ value: u.id, label: [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email }))}
            clearable
            clearLabel='Unassigned'
            placeholder='Unassigned'
            searchPlaceholder='Search people…'
            disabled={saving}
            ariaLabel='Investigator'
          />
        </Field>
        <Field>
          <FieldLabel>Due</FieldLabel>
          <DateInput
            type='date'
            value={incident.investigationDueAt ? incident.investigationDueAt.slice(0, 10) : ''}
            disabled={saving}
            onChange={e => patch({ investigation_due_at: e.target.value || null }, 'Due date updated.')} />
        </Field>
        <Field>
          <FieldLabel>Root cause</FieldLabel>
          <SearchableSelect
            value={incident.rootCause}
            onChange={val => patch({ root_cause: val }, 'Root cause recorded.')}
            options={Object.entries(ROOT_CAUSES).map(([v, l]) => ({ value: v, label: l }))}
            clearable
            clearLabel='Not set'
            placeholder='Not set'
            disabled={saving}
            ariaLabel='Root cause'
          />
        </Field>
      </FieldRow>

      <Field>
        <FieldLabel>Findings</FieldLabel>
        <NoteArea
          rows={3}
          value={noteDraft}
          placeholder='Investigation findings, contributing factors, immediate causes…'
          onChange={e => setNoteDraft(e.target.value)} />
        {noteDraft !== (incident.investigationNote || '') && (
          <SaveNoteBtn disabled={saving} onClick={() => patch({ investigation_note: noteDraft }, 'Findings saved.')}>
            Save findings
          </SaveNoteBtn>
        )}
      </Field>

      <RiddorBlock $urgent={riddorUrgent}>
        <RiddorHead>
          <GavelRoundedIcon style={{ fontSize: 15 }} />
          <strong>RIDDOR</strong>
          <RiddorToggle
            type='button'
            $on={incident.riddorReportable}
            disabled={saving}
            onClick={() => patch({ riddor_reportable: !incident.riddorReportable }, 'RIDDOR flag updated.')}>
            {incident.riddorReportable ? 'Reportable' : 'Not reportable'}
          </RiddorToggle>
        </RiddorHead>
        {incident.riddorReportable && (
          <RiddorBody>
            <Field>
              <FieldLabel>Statutory deadline</FieldLabel>
              <DateInput
                type='date'
                value={incident.riddorDeadline || ''}
                disabled={saving}
                onChange={e => patch({ riddor_deadline: e.target.value || null }, 'Deadline updated.')} />
            </Field>
            <Field>
              <FieldLabel>Report reference</FieldLabel>
              <RefRow>
                <TextInput
                  value={riddorRefDraft}
                  placeholder='HSE reference once submitted'
                  onChange={e => setRiddorRefDraft(e.target.value)} />
                {riddorRefDraft !== (incident.riddorReference || '') && (
                  <SaveNoteBtn
                    disabled={saving}
                    onClick={() => patch({
                      riddor_reference: riddorRefDraft || null,
                      riddor_reported_at: riddorRefDraft ? new Date().toISOString().slice(0, 10) : null,
                    }, 'RIDDOR report recorded.')}>
                    Save
                  </SaveNoteBtn>
                )}
              </RefRow>
            </Field>
            {incident.riddorReportedAt ? (
              <RiddorDone>Reported {new Date(incident.riddorReportedAt).toLocaleDateString('en-GB')}{incident.riddorReference ? ` · ref ${incident.riddorReference}` : ''}</RiddorDone>
            ) : riddorUrgent ? (
              <RiddorWarn>Deadline imminent or passed with no report reference recorded.</RiddorWarn>
            ) : null}
          </RiddorBody>
        )}
      </RiddorBlock>

      <WitnessBlock>
        <PanelHead>
          <PanelTitle as='h4' $small>Witness statements · {statements.length}</PanelTitle>
          <AddWitnessBtn type='button' onClick={() => setAddingWitness(a => !a)}>
            <AddRoundedIcon style={{ fontSize: 13 }} />
            Add statement
          </AddWitnessBtn>
        </PanelHead>
        {addingWitness && (
          <WitnessForm>
            <WitnessRow>
              <TextInput
                value={witnessDraft.name}
                placeholder='Witness name'
                autoFocus
                onChange={e => setWitnessDraft(d => ({ ...d, name: e.target.value }))} />
              <TextInput
                value={witnessDraft.contact}
                placeholder='Contact (optional)'
                onChange={e => setWitnessDraft(d => ({ ...d, contact: e.target.value }))} />
            </WitnessRow>
            <NoteArea
              rows={3}
              value={witnessDraft.statement}
              placeholder='What did they see or hear? Record in their own words.'
              onChange={e => setWitnessDraft(d => ({ ...d, statement: e.target.value }))} />
            <WitnessActions>
              <CancelBtn type='button' onClick={() => setAddingWitness(false)}>Cancel</CancelBtn>
              <SaveNoteBtn
                disabled={!witnessDraft.name.trim() || !witnessDraft.statement.trim()}
                onClick={addWitness}>
                Record statement
              </SaveNoteBtn>
            </WitnessActions>
          </WitnessForm>
        )}
        {statements.map(s => (
          <StatementCard key={s.id}>
            <StatementHead>
              <strong>{s.witnessName}</strong>
              {s.witnessContact && <span>{s.witnessContact}</span>}
              <StatementMeta>
                recorded by {s.recordedBy || '—'} · {s.createdAt ? new Date(s.createdAt).toLocaleString('en-GB') : ''}
              </StatementMeta>
            </StatementHead>
            <StatementBody>{s.statement}</StatementBody>
          </StatementCard>
        ))}
      </WitnessBlock>
    </Panel>
  )
}

export default IncidentInvestigationPanel

const Panel = styled.div`
  border: 1px solid #ececec;
  border-radius: 12px;
  padding: 16px 18px;
  display: flex; flex-direction: column; gap: 14px;
  background: #fafbfc;
`
const PanelHead = styled.div`
  display: flex; align-items: center; gap: 10px;
`
const PanelTitle = styled.h3`
  margin: 0;
  font-size: ${({ $small }) => $small ? '12px' : '13px'};
  font-weight: 800;
  letter-spacing: 0.10em;
  text-transform: uppercase;
  color: #0C1229;
`
const OverdueChip = styled.span`
  padding: 2px 9px;
  background: rgba(204,34,0,0.08);
  color: #b00020;
  border: 1px solid rgba(204,34,0,0.28);
  border-radius: 999px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`
const FieldRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 10px;
`
const Field = styled.div` display: flex; flex-direction: column; gap: 4px; `
const FieldLabel = styled.label`
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 10px;
  letter-spacing: 0.10em;
  text-transform: uppercase;
  font-weight: 800;
  color: #6b7280;
`
const DateInput = styled.input`
  font: inherit;
  padding: 7px 10px;
  border: 1px solid #ececec;
  border-radius: 8px;
  font-size: 12.5px;
  background: #fff;
`
const TextInput = styled.input`
  font: inherit;
  flex: 1;
  padding: 8px 10px;
  border: 1px solid #ececec;
  border-radius: 8px;
  font-size: 12.5px;
  background: #fff;
  outline: none;
  &:focus { border-color: #33becc; }
`
const NoteArea = styled.textarea`
  font: inherit;
  padding: 9px 11px;
  border: 1px solid #ececec;
  border-radius: 8px;
  font-size: 12.5px;
  background: #fff;
  resize: vertical;
  outline: none;
  &:focus { border-color: #33becc; }
`
const SaveNoteBtn = styled.button`
  font: inherit;
  align-self: flex-start;
  padding: 6px 14px;
  background: linear-gradient(135deg, #33becc 0%, #2aa6b3 100%);
  color: #fff;
  border: none;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 800;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`
const CancelBtn = styled.button`
  font: inherit;
  padding: 6px 14px;
  background: transparent;
  border: 1px solid #ececec;
  color: #0C1229;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 700;
  cursor: pointer;
`
const RiddorBlock = styled.div`
  border: 1px solid ${({ $urgent }) => $urgent ? 'rgba(204,34,0,0.34)' : '#ececec'};
  background: ${({ $urgent }) => $urgent ? 'rgba(204,34,0,0.04)' : '#fff'};
  border-radius: 10px;
  padding: 12px 14px;
  display: flex; flex-direction: column; gap: 10px;
`
const RiddorHead = styled.div`
  display: flex; align-items: center; gap: 8px;
  font-size: 12px;
  strong { letter-spacing: 0.08em; }
`
const RiddorToggle = styled.button`
  font: inherit;
  margin-left: auto;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  cursor: pointer;
  background: ${({ $on }) => $on ? 'rgba(204,34,0,0.08)' : '#f4f4f6'};
  color: ${({ $on }) => $on ? '#b00020' : '#6b7280'};
  border: 1px solid ${({ $on }) => $on ? 'rgba(204,34,0,0.30)' : '#e4e4e7'};
`
const RiddorBody = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.4fr;
  gap: 10px;
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`
const RefRow = styled.div` display: flex; gap: 6px; align-items: center; `
const RiddorDone = styled.span`
  grid-column: 1 / -1;
  font-size: 11.5px;
  font-weight: 700;
  color: #2aa6b3;
`
const RiddorWarn = styled.span`
  grid-column: 1 / -1;
  font-size: 11.5px;
  font-weight: 700;
  color: #b00020;
`
const WitnessBlock = styled.div` display: flex; flex-direction: column; gap: 10px; `
const AddWitnessBtn = styled.button`
  font: inherit;
  margin-left: auto;
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 11px;
  background: transparent;
  border: 1px dashed #cbcbd2;
  color: #6b7280;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  cursor: pointer;
  &:hover { border-color: #33becc; color: #1f8c97; }
`
const WitnessForm = styled.div`
  display: flex; flex-direction: column; gap: 8px;
  padding: 12px;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 10px;
`
const WitnessRow = styled.div`
  display: flex; gap: 8px;
  @media (max-width: 560px) { flex-direction: column; }
`
const WitnessActions = styled.div` display: flex; gap: 8px; justify-content: flex-end; `
const StatementCard = styled.div`
  padding: 12px 14px;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 10px;
  display: flex; flex-direction: column; gap: 6px;
`
const StatementHead = styled.div`
  display: flex; align-items: baseline; gap: 10px;
  font-size: 12.5px;
  flex-wrap: wrap;
  span { color: #6b7280; font-size: 11.5px; }
`
const StatementMeta = styled.span`
  margin-left: auto;
  font-size: 10.5px !important;
`
const StatementBody = styled.p`
  margin: 0;
  font-size: 12.5px;
  line-height: 1.55;
  color: #1e293b;
  white-space: pre-wrap;
`
